import * as React from 'react';
import Head from 'next/head';
import { Button } from 'evergreen-ui';
import { GetServerSideProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import ChangeLanguage from 'components/authentication/ChangeLanguage';
import InputText from 'components/form/InputText';
import LoadingCover from 'components/layout/LoadingCover';
import styles from 'src/css/authentication';
import { useTranslation } from 'src/i18n';

type State = {
  email: string;
  isFailure: boolean;
  isRequestLimited: boolean;
  isSuccess: boolean;
  isSubmitting: boolean;
};

enum ActionType {
  SetEmail = 'set-email',
  SetPassword = 'set-password',
  OnSuccess = 'on-success',
  OnFailure = 'on-failure',
  OnRequestLimited = 'on-request-limited',
  OnSubmit = 'on-submit',
}

type Action =
  | { type: ActionType.SetEmail, payload: string }
  | { type: ActionType.OnSuccess }
  | { type: ActionType.OnFailure }
  | { type: ActionType.OnRequestLimited }
  | { type: ActionType.OnSubmit };

const initialState: State = {
  email: '',
  isSuccess: false,
  isFailure: false,
  isRequestLimited: false,
  isSubmitting: false,
};

const reducer: React.Reducer<State, Action> = (
  state: State,
  action: Action,
): State => {
  const patch = (diff: Partial<State>): State => ({ ...state, ...diff });
  switch (action.type) {
    case ActionType.SetEmail:
      return patch({ email: action.payload });
    case ActionType.OnSuccess:
      return patch({ isSuccess: true, isSubmitting: false });
    case ActionType.OnFailure:
      return patch({ isFailure: true, isSubmitting: false });
    case ActionType.OnRequestLimited:
      return patch({ isRequestLimited: true, isSubmitting: false });
    case ActionType.OnSubmit:
      // 前回の結果はすべて消す。成功を残すと、再送信が失敗したときに成功と失敗が並ぶ
      return patch({ isSubmitting: true, isSuccess: false, isFailure: false, isRequestLimited: false });
  }
};

export const getServerSideProps: GetServerSideProps = async ({ locale }) => ({  props: { ...(await serverSideTranslations(locale || 'ja')) } });

const PasswordResetRequest: React.FC = () => {
  const { t } = useTranslation();
  const [state, dispatch] = React.useReducer(reducer, initialState);
  React.useEffect(() => {
    const resetRequest = async (): Promise<void> => {
      const res = await fetch('/api/users/password-reset-request', {
        method: 'POST',
        body: JSON.stringify({ email: state.email }),
      });
      if (res.status === 204) {
        return dispatch({ type: ActionType.OnSuccess });
      }
      if (res.status === 429) {
        return dispatch({ type: ActionType.OnRequestLimited });
      }
      return dispatch({ type: ActionType.OnFailure });
    };
    if (state.isSubmitting) { resetRequest(); }
  }, [state.isSubmitting, state.email]);

  const setEmail = React.useCallback((value: string) => {
    dispatch({ type: ActionType.SetEmail, payload: value });
  }, [dispatch]);
  const onSubmit = React.useCallback(() => {
    dispatch({ type: ActionType.OnSubmit });
  }, [dispatch]);
  const isInvalid = state.isFailure || state.isRequestLimited;
  const validationMessage = React.useMemo(() => {
    if (state.isRequestLimited) { return t('check.request.limit'); }
    if (state.isFailure) { return t('check.faild'); }
    return '';
  }, [state.isRequestLimited, state.isFailure, t]);
  return (
    <>
      <Head>
        <title>{t('reset.password')}</title>
      </Head>
      {state.isSubmitting && <LoadingCover />}
      <div className="signin-container">
        <div className="signin-inner">
          <div className="signin-title">{t('reset.password')}</div>
          <div className="signin-form">
            <InputText
              id="email"
              inputValue={state.email}
              label={t('users.email')}
              onChangeValue={setEmail}
              isInvalid={isInvalid}
              validationMessage={validationMessage}
            />
            <div className="submit-container">
              <Button
                appearance="primary"
                onClick={onSubmit}
                marginLeft="auto">
                {(t('change.password.request'))}
              </Button>
            </div>
            {state.isSuccess && (
              <div className="message-success">
                <span>{t('check.password')}</span>
              </div>
            )}
          </div>
        </div>
        <ChangeLanguage />
      </div>
      <style jsx>{styles}</style>
      <style jsx>{`
        /* InputText は p[role=alert] を white-space: nowrap で 1 行に固定する。
           上限の文言は入力欄の幅に収まらず右端で切れるため、この画面だけ折り返す */
        .signin-form :global(p[role=alert]) {
          white-space: normal;
        }
      `}</style>
    </>
  );
};

export default PasswordResetRequest;
